(() => {
  if (customElements.get('alpe-all-reviews')) return;
  class AlpeAllReviews extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.labels = JSON.parse(this.querySelector('[data-review-labels]').textContent);
      this.batchSize = Math.max(0, Number.parseInt(this.dataset.batchSize, 10) || 0);
      this.grid = this.querySelector('[data-grid]');
      this.status = this.querySelector('[data-status]');
      this.dialog = this.querySelector('[data-lightbox]');
      this.resize = new ResizeObserver(entries => entries.forEach(entry => this.sizeCard(entry.target)));
      this.querySelector('[data-search]').addEventListener('input', () => this.paint());
      this.querySelectorAll('select').forEach(input => input.addEventListener('change', () => this.paint()));
      this.querySelector('[data-more]').addEventListener('click', () => this.showMore());
      this.querySelector('[data-retry]').addEventListener('click', () => this.load());
      this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', event => {if (event.target === this.dialog) this.dialog.close();});
      this.load();
    }
    disconnectedCallback() {this.controller?.abort(); this.resize?.disconnect(); this.ready = false;}
    node(tag, cls = '', text) {const node = document.createElement(tag); node.className = cls; if (text != null) node.textContent = text; return node;}
    async get(url, signal) {
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const response = await fetch(url, {headers: {revision: '2026-07-15', accept: 'application/vnd.api+json'}, signal: AbortSignal.any([signal, AbortSignal.timeout(15000)])});
          if (!response.ok) throw new Error(`Reviews ${response.status}`);
          const data = await response.json();
          if (!Array.isArray(data.data)) throw new Error('Invalid reviews');
          return data;
        } catch (error) {
          if (signal.aborted || attempt === 2) throw error;
          await new Promise(resolve => setTimeout(resolve, 750 * (attempt + 1)));
        }
      }
    }
    async collect(filter, signal) {
      let url = new URL('https://fast.a.klaviyo.com/client/reviews/');
      url.search = new URLSearchParams({company_id: this.dataset.company, 'page[size]': '100', filter, sort: '-created'});
        const reviews = new Map(), visited = new Set();
        while (url) {
          if (url.origin !== 'https://fast.a.klaviyo.com' || !/^\/client\/reviews\/?$/.test(url.pathname) || visited.has(url.href)) throw new Error('Invalid pagination');
          visited.add(url.href);
          const data = await this.get(url, signal);
          data.data.forEach(review => {
            const value = review.attributes;
            if (['published', 'featured'].includes(value.status?.value) && ['review', 'rating', 'store'].includes(value.review_type) && Number(value.rating) >= 1 && Number(value.rating) <= 5) reviews.set(review.id, {id: review.id, ...value});
          });
          url = data.links?.next ? new URL(data.links.next) : null;
          if (url) {
            if (!['a.klaviyo.com', 'fast.a.klaviyo.com'].includes(url.hostname)) throw new Error('Invalid pagination host');
            url.hostname = 'fast.a.klaviyo.com';
            url.searchParams.set('company_id', this.dataset.company);
          }
        }
      return reviews;
    }
    async load() {
      this.controller?.abort(); const controller = new AbortController(); this.controller = controller;
      this.setAttribute('aria-busy', 'true'); this.querySelector('[data-retry]').hidden = true;
      this.status.hidden = false; this.status.textContent = this.loadingText ||= this.status.textContent;
      try {
        const [reviews, incentivized] = await Promise.all([
          this.collect("equals(status,'published')", controller.signal),
          this.collect("and(equals(status,'published'),equals(incentivized,true))", controller.signal)
        ]);
        reviews.forEach(review => {review.incentivized = incentivized.has(review.id);});
        if (controller.signal.aborted || !this.isConnected) return;
        this.reviews = [...reviews.values()];
        this.querySelector('[data-summary]').hidden = this.dataset.headingOnly === 'true' || !this.reviews.length;
        this.querySelector('[data-filters]').hidden = this.dataset.headingOnly === 'true' || !this.reviews.length;
        const average = this.reviews.length ? this.reviews.reduce((sum, r) => sum + Number(r.rating), 0) / this.reviews.length : 0;
        this.querySelector('[data-average]').textContent = `${average.toFixed(1).replace('.', ',')} / 5`;
        this.querySelector('[data-stars]').replaceChildren(this.stars(average));
        const count = this.labels[new Intl.PluralRules('sl').select(this.reviews.length)] || this.labels.other;
        this.querySelector('[data-count]').textContent = count.replace('__number__', this.reviews.length);
        this.paint(); this.dataset.loaded = 'true';
      } catch (error) {
        if (controller.signal.aborted || !this.isConnected) return;
        this.status.textContent = this.labels.error; this.querySelector('[data-retry]').hidden = false;
      } finally {if (this.controller === controller) this.setAttribute('aria-busy', 'false');}
    }
    stars(rating) {
      const stars = this.node('span', 'all-reviews__stars'); stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', this.labels.average.replace('__number__', Number(rating).toFixed(1).replace('.', ',')));
      for (let index = 0; index < 5; index++) {
        const ns = 'http://www.w3.org/2000/svg', svg = document.createElementNS(ns, 'svg'), background = document.createElementNS(ns, 'path'), foreground = document.createElementNS(ns, 'path');
        svg.setAttribute('viewBox', '0 0 20 20'); svg.setAttribute('aria-hidden', 'true');
        const path = 'M10 1.5l2.63 5.33 5.88.85-4.25 4.15 1 5.85L10 14.92l-5.26 2.76 1-5.85-4.25-4.15 5.88-.85Z';
        background.setAttribute('d', path); background.setAttribute('fill', '#ddd'); foreground.setAttribute('d', path); foreground.setAttribute('fill', '#ffb400');
        foreground.style.clipPath = `inset(0 ${100 - Math.min(1, Math.max(0, rating - index)) * 100}% 0 0)`;
        svg.append(background, foreground); stars.append(svg);
      }
      return stars;
    }
    safeUrl(raw) {try {const url = new URL(raw); return ['https:', 'http:'].includes(url.protocol) ? url.href : null;} catch {return null;}}
    productPreviewUrl(raw, width) {
      const url = new URL(raw);
      const shopify = url.hostname === 'cdn.shopify.com' || ((url.hostname === location.hostname || url.hostname.endsWith('.myshopify.com')) && url.pathname.startsWith('/cdn/shop/'));
      if (!shopify) return raw;
      url.searchParams.set('width', String(width));
      url.searchParams.delete('height');
      return url.href;
    }
    image(uuid, width = 400) {return `https://reviews-media.services.klaviyo.com/abc/width:${width}/height:${width}/resizing_type:fit/plain/https://klaviyo.s3.amazonaws.com/reviews/images/${encodeURIComponent(uuid)}`;}
    reviewUrl(review) {
      const raw = this.safeUrl(review.product?.url);
      if (!raw || !/^\d+$/.test(String(review.id))) return null;
      const source = new URL(raw);
      if (![location.hostname, 'alpe.cc', 'www.alpe.cc', 'alpe-cc.myshopify.com'].includes(source.hostname)) return null;
      const path = source.pathname.match(/\/products\/([^/]+)\/?$/);
      if (!path) return null;
      const url = new URL(`${this.dataset.root || '/'}products/${path[1]}`, location.origin);
      url.searchParams.set('alpe_review', review.id);
      url.hash = `alpe-review-${review.id}`;
      return url.href;
    }
    card(review) {
      const card = this.node('article', 'all-reviews__card'); card.dataset.reviewId = review.id;
      const inner = this.node('div', 'all-reviews__inner'), top = this.node('div', 'all-reviews__top'); top.append(this.stars(Number(review.rating)));
      const date = new Date(review.created);
      if (!Number.isNaN(date.getTime())) {const time = this.node('time', '', new Intl.DateTimeFormat(document.documentElement.lang || 'sl', {day: 'numeric', month: 'short', year: 'numeric'}).format(date)); time.dateTime = date.toISOString(); top.append(time);}
      inner.append(top, this.node('p', 'all-reviews__author', typeof review.author === 'string' ? review.author : review.author?.name || ''));
      if (review.incentivized) inner.append(this.node('span', 'all-reviews__verified', this.labels.rewarded));
      if (review.verified) inner.append(this.node('span', 'all-reviews__verified', `✓ ${this.labels.verified}`));
      if (review.title) inner.append(this.node(this.dataset.headingOnly === 'true' ? 'h3' : 'h2', 'all-reviews__title', review.title));
      if (review.content) inner.append(this.node('p', 'all-reviews__content', review.content));
      if (review.images?.length) {
        const photos = this.node('div', 'all-reviews__photos');
        review.images.forEach(uuid => {
          const button = this.node('button', 'all-reviews__photo'); button.type = 'button'; button.setAttribute('aria-label', review.product?.name || this.labels.store);
          const image = this.node('img'); image.src = this.image(uuid); image.srcset = [400, 800, 1200].map((width, index) => `${this.image(uuid, width)} ${index + 1}x`).join(', '); image.width = 100; image.height = 100; image.loading = 'lazy'; image.decoding = 'async'; image.alt = review.product?.name || this.labels.store;
          image.addEventListener('error', () => {image.removeAttribute('srcset'); image.src = this.image(uuid);}, {once: true});
          button.append(image); button.addEventListener('click', () => {const large = this.dialog.querySelector('img'); large.src = this.image(uuid, 1200); large.alt = image.alt; this.dialog.showModal();}); photos.append(button);
        }); inner.append(photos);
      }
      const product = this.node('div', 'all-reviews__product');
      const productImage = this.safeUrl(review.product?.image_url);
      if (productImage) {
        const image = this.node('img'); image.src = this.productPreviewUrl(productImage, 96);
        if (image.src !== productImage) image.srcset = [96, 192, 288].map((width, index) => `${this.productPreviewUrl(productImage, width)} ${index + 1}x`).join(', ');
        image.addEventListener('error', () => {image.removeAttribute('srcset'); image.src = productImage;}, {once: true});
        image.width = 44; image.height = 54; image.alt = ''; image.loading = 'lazy'; image.decoding = 'async'; product.append(image);
      }
      product.append(this.node('span', '', review.product?.name || this.labels.store)); inner.append(product);
      if (review.public_reply?.content) {const reply = this.node('div', 'all-reviews__reply'); reply.append(this.node('strong', '', review.public_reply.author || 'Alpe.cc'), this.node('p', '', review.public_reply.content)); inner.append(reply);}
      const reviewUrl = this.reviewUrl(review);
      if (reviewUrl) {
        const link = this.node('a', 'all-reviews__review-link', this.labels.viewReview);
        link.href = reviewUrl;
        link.setAttribute('aria-label', `${this.labels.viewReview}: ${review.author?.name || review.author || ''} — ${review.product.name}`);
        const arrow = this.node('span', '', '→'); arrow.setAttribute('aria-hidden', 'true'); link.append(arrow);
        inner.append(link);
      }
      card.append(inner); return card;
    }
    sizeCard(inner) {
      const style = getComputedStyle(this.grid), gap = parseFloat(style.rowGap), row = parseFloat(style.gridAutoRows);
      inner.parentElement.style.gridRowEnd = `span ${Math.ceil((inner.getBoundingClientRect().height + gap) / (row + gap))}`;
    }
    syncMore() {
      this.querySelector('[data-more-wrapper]').hidden = !this.batchSize || this.limit >= this.filtered.length;
    }
    showMore() {
      if (!this.filtered || !this.batchSize || this.limit >= this.filtered.length) return;
      const cards = this.filtered.slice(this.limit, this.limit + this.batchSize).map(review => this.card(review));
      this.limit += cards.length; this.grid.append(...cards);
      cards.forEach(card => {const inner = card.querySelector('.all-reviews__inner'); this.sizeCard(inner); this.resize.observe(inner);});
      this.syncMore();
      this.querySelector('[data-revealed]').textContent = this.labels.filtered.replace('__number__', this.grid.children.length);
      if (cards[0]) {cards[0].tabIndex = -1; cards[0].focus({preventScroll: true});}
    }
    paint() {
      if (!this.reviews) return;
      const search = this.querySelector('[data-search]').value.trim().toLocaleLowerCase(), rating = this.querySelector('[data-rating]').value, sort = this.querySelector('[data-sort]').value;
      const filtered = this.reviews.filter(r => (!rating || Number(r.rating) === Number(rating)) && (!search || [r.author, r.content, r.title, r.product?.name].filter(Boolean).join(' ').toLocaleLowerCase().includes(search)));
      filtered.sort((a, b) => (sort === 'highest' ? b.rating - a.rating : sort === 'lowest' ? a.rating - b.rating : 0) || new Date(b.created) - new Date(a.created) || String(a.id).localeCompare(String(b.id)));
      this.filtered = filtered; this.limit = this.batchSize || filtered.length;
      this.resize.disconnect(); this.grid.replaceChildren(...filtered.slice(0, this.limit).map(r => this.card(r)));
      this.syncMore();
      this.grid.querySelectorAll('.all-reviews__inner').forEach(inner => {this.sizeCard(inner); this.resize.observe(inner);});
      this.status.textContent = filtered.length ? this.labels.filtered.replace('__number__', filtered.length) : this.labels.empty;
      this.status.hidden = filtered.length === this.reviews.length && Boolean(filtered.length);
    }
  }
  customElements.define('alpe-all-reviews', AlpeAllReviews);
})();
